import { useCallback, useEffect, useRef } from 'react'

import { addEventListenerPassive } from '@/core/utils/dom/utils'

import useDebounce from '../useDebounce'

interface Props<Ref> {
  ref?: Ref
  onRight: (event?: KeyboardEvent) => void
  onLeft: (event?: KeyboardEvent) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useKeyDownArrows<Ref extends React.MutableRefObject<any>>({
  ref,
  onRight,
  onLeft,
}: Props<Ref>) {
  const newRef = useRef(null) as Ref

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === 39 || e.key === 'ArrowRight') {
        onRight(e)
      }

      if (e.keyCode === 37 || e.key === 'ArrowLeft') {
        onLeft(e)
      }
    },
    [onLeft, onRight],
  )

  const debounceScroll = useDebounce(handleKeyDown)

  useEffect(() => {
    const refKeydown = ref || newRef

    addEventListenerPassive('keydown', debounceScroll, refKeydown.current)

    return () => refKeydown.current?.removeEventListener('keydown', debounceScroll)
  }, [debounceScroll, ref])

  return { ref: ref || newRef }
}
